import { Injectable } from '@angular/core';
import { CommonConstant } from './constants';
import { CommonUtility } from './utility';

@Injectable({ providedIn: 'root' })
export class UserAuthService {

    saveToken(token: string): void {
        sessionStorage.setItem(CommonConstant.token, token);
    }

    saveOrgCode(orgCode: string): void {
        sessionStorage.setItem(CommonConstant.orgCode, orgCode);
    }

    saveSchoolName(schoolName: string): void {
        sessionStorage.setItem(CommonConstant.school, schoolName);
    }

    saveEmployeeName(employeeName: string): void {
        sessionStorage.setItem(CommonConstant.employeeName, employeeName);
    }

    saveEmployeeId(employeeId: any): void {
        // sessionStorage.setItem coerces its value with String(), so a JS
        // null/undefined here (a login with no matching HR employee profile)
        // was stored as the literal 4-character string "null" - truthy, and
        // sent as a real query param (employeeId=null) to endpoints expecting
        // a numeric id, which then failed with NumberFormatException.
        if (employeeId === null || employeeId === undefined) {
            sessionStorage.removeItem(CommonConstant.employeeId);
        } else {
            sessionStorage.setItem(CommonConstant.employeeId, employeeId);
        }
    }

    saveStudentProfileId(studentId: any): void {
        if (studentId === null || studentId === undefined) {
            sessionStorage.removeItem(CommonConstant.studentProfileId);
        } else {
            sessionStorage.setItem(CommonConstant.studentProfileId, studentId);
        }
    }

    saveAppName(appName: string): void {
        sessionStorage.setItem(CommonConstant.appName, appName);
    }

    saveUserName(userName: string): void {
        sessionStorage.setItem(CommonConstant.userName, userName);
    }

    saveSupportSessionId(sessionId: string): void {
        if (sessionId) {
            sessionStorage.setItem(CommonConstant.supportSessionId, sessionId);
        } else {
            sessionStorage.removeItem(CommonConstant.supportSessionId);
        }
    }

    /** Marks this tab as a System Admin support-session handoff. */
    saveSupportSession(isSupport: boolean): void {
        if (isSupport) {
            sessionStorage.setItem(CommonConstant.supportSession, 'Y');
        } else {
            sessionStorage.removeItem(CommonConstant.supportSession);
        }
    }

    saveImpersonatedSchool(school: any): void {
        if (school) {
            sessionStorage.setItem(CommonConstant.impersonatedSchool, JSON.stringify(school));
        } else {
            sessionStorage.removeItem(CommonConstant.impersonatedSchool);
        }
    }

    getToken(): string | null {
        return sessionStorage.getItem(CommonConstant.token) || this.getTokenFromUrl();
    }

    getTokenFromUrl(): string | null {
        return new URLSearchParams(window.location.search).get('token');
    }

    getOrgCode(): string | null {
        return sessionStorage.getItem(CommonConstant.orgCode)
            || new URLSearchParams(window.location.search).get('code');
    }

    getSchoolName(): string | null {
        return sessionStorage.getItem(CommonConstant.school);
    }

    getEmployeeName(): string | null {
        return sessionStorage.getItem(CommonConstant.employeeName);
    }

    getEmployeeId(): string | null {
        // A session that already stored the literal string "null" (see
        // saveEmployeeId) needs this read-side guard too - it isn't cleared
        // until the value is next saved, which for an idle tab may be never.
        const value = sessionStorage.getItem(CommonConstant.employeeId);
        return value && value !== 'null' ? value : null;
    }

    getStudentProfileId(): string | null {
        const value = sessionStorage.getItem(CommonConstant.studentProfileId);
        return value && value !== 'null' ? value : null;
    }

    getAppName(): string | null {
        return sessionStorage.getItem(CommonConstant.appName);
    }

    getUserName(): string | null {
        return sessionStorage.getItem(CommonConstant.userName);
    }

    getSupportSessionId(): string | null {
        return sessionStorage.getItem(CommonConstant.supportSessionId);
    }

    /** True when this tab was opened through a System Admin support session. */
    isSupportSession(): boolean {
        return sessionStorage.getItem(CommonConstant.supportSession) === 'Y';
    }

    getImpersonatedSchool(): any {
        const school = sessionStorage.getItem(CommonConstant.impersonatedSchool);
        return CommonUtility.isEmpty(school) ? null : JSON.parse(school!);
    }

    isLoggedIn(): boolean {
        return !CommonUtility.isNull(this.getToken());
    }

    logout(): void {
        sessionStorage.clear();
    }
}
